import { pdf } from '@react-pdf/renderer';
import type React from 'react';

export async function downloadReactPdf(options: {
  filename: string;
  reactPdfDocument: React.ReactElement;
}) {
  const { filename, reactPdfDocument } = options;

  const blob = await pdf(reactPdfDocument).toBlob();
  const url = URL.createObjectURL(blob);

  try {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Opens the generated PDF in a new tab and triggers the browser print dialog.
 * Note: To avoid popup blockers, call this directly from a user gesture (button click).
 */
export async function printReactPdf(options: { reactPdfDocument: React.ReactElement; title?: string }) {
  const { reactPdfDocument, title = 'print' } = options;

  // Open a blank window first (more likely to pass popup blockers)
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Popup blocked: unable to open print window');
  }

  const blob = await pdf(reactPdfDocument).toBlob();
  const url = URL.createObjectURL(blob);

  // Render an iframe with the PDF and print once it loads.
  // Some browsers don't reliably fire "load" for embedded PDF; we also use a fallback timeout.
  printWindow.document.open();
  printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${String(title).replace(/</g, '&lt;')}</title>
    <style>
      html, body { margin: 0; padding: 0; height: 100%; }
      iframe { border: 0; width: 100%; height: 100%; }
    </style>
  </head>
  <body>
    <iframe id="pdf" src="${url}"></iframe>
    <script>
      const iframe = document.getElementById('pdf');
      const doPrint = () => {
        try {
          // Prefer printing the iframe content (PDF viewer) to avoid printing the wrapper page
          const w = iframe && iframe.contentWindow;
          if (w && w.print) {
            w.focus();
            w.print();
            return;
          }
        } catch (e) {}
        try {
          window.focus();
          window.print();
        } catch (e) {}
      };
      iframe && iframe.addEventListener && iframe.addEventListener('load', () => setTimeout(doPrint, 350));
      // Fallback in case "load" doesn't fire for PDF viewers
      setTimeout(doPrint, 1800);
    </script>
  </body>
</html>`);
  printWindow.document.close();

  // Revoke blob URL later to keep it available for the print viewer.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function formatExportTimestamp(date: Date = new Date()) {
  // Example: "24.06.2025, 11:15 AM" depending on locale.
  return date.toLocaleString();
}


